<template>
  <!-- 画像圧縮モーダル -->
  <div :id="modalId" class="c-modal c-modal--image-compress">
    <div class="c-modal__bg"></div>
    <div class="c-modal__content c-modal__content--large">
      <div class="c-modal__close" @click="cancelCompress">
        <div class="c-icon c-icon--white u-wh14">
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
          >
            <use xlink:href="/assets/img/sprite.svg#close" />
          </svg>
        </div>
      </div>
      <div class="c-modal__inner">
        <div class="c-modal__title c-modal__title--bold">{{ modalTitle }}</div>
        <div class="c-modal__description" v-html="modalDescription"></div>
        <div class="c-modal__data">
          <div class="c-image-compress">
            <div class="c-image-compress__preview">
              <img v-if="imageSource" :src="imageSource" alt="" />
            </div>
            <div v-if="isProcessing" class="c-image-compress__processing">
              圧縮処理中...
            </div>
            <div v-if="compressionError" class="c-image-compress__error">
              {{ compressionError }}
            </div>
          </div>
        </div>
        <div class="c-modal__btns">
          <button
            class="c-btn c-btn--modal c-btn--gray u-w140 u-h34 u-fs14"
            type="button"
            @click="cancelCompress"
          >
            <p class="c-btn__text">キャンセル</p>
          </button>
          <button
            class="c-btn c-btn--modal c-btn--blue u-w140 u-h34 u-fs14"
            :disabled="isProcessing"
            type="button"
            @click="confirmCompress"
          >
            <p class="c-btn__text">確定</p>
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- /画像圧縮モーダル -->
</template>

<script>
export default {
  props: {
    modalId: {
      type: String,
      default: 'imageCompressModal',
    },
    modalTitle: {
      type: String,
      default: '大型画像最適化',
    },
    modalDescription: {
      type: String,
      default: '画像サイズが128KBを超えています。<br>128KB以下に自動変換されます。',
    },
    maxFileSize: {
      type: Number,
      default: 131072, // 128KB
    },
    preferredFormat: {
      type: String,
      default: 'png',
      validator: (value) => ['jpeg', 'png'].includes(value),
    },
  },
  emits: ["cancelled","compressed"],
  data() {
    return {
      imageSource: null,
      sourceWidth: 0,
      sourceHeight: 0,
      isProcessing: false,
      compressionError: null,
    };
  },
  methods: {
    /**
     * モーダルを表示
     * @param {File} file - 圧縮する画像ファイル
     */
    showModal(file) {
      this.compressionError = null;
      this.isProcessing = false;

      const reader = new FileReader();
      reader.addEventListener('load', () => {
        const img = new Image();
        img.onload = () => {
          this.sourceWidth = img.width;
          this.sourceHeight = img.height;
          this.imageSource = reader.result;
          $(`#${this.modalId}`).addClass('is-active');
        };
        img.src = reader.result;
      });
      reader.readAsDataURL(file);
    },

    /**
     * モーダルを閉じる
     */
    closeModal() {
      $(`#${this.modalId}`).removeClass('is-active');
      this.imageSource = null;
    },

    /**
     * 圧縮キャンセル
     */
    cancelCompress() {
      this.$emit('cancelled');
      this.closeModal();
    },

    /**
     * 圧縮確定
     */
    async confirmCompress() {
      if (this.isProcessing) {
        return;
      }
      this.isProcessing = true;
      this.compressionError = null;

      try {
        // 元画像を元サイズ・元縦横比のまま Canvas に描画
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = this.imageSource;
        });
        const canvas = document.createElement('canvas');
        canvas.width = this.sourceWidth;
        canvas.height = this.sourceHeight;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, this.sourceWidth, this.sourceHeight);

        const compressedBlob = await this.compressToTargetSize(
          canvas,
          this.maxFileSize,
          this.preferredFormat
        );

        if (!compressedBlob) {
          const maxSizeKB = Math.round(this.maxFileSize / 1024);
          this.compressionError =
            `画像を${maxSizeKB}KB以下に圧縮できませんでした。別の画像をお試しください。`;
          this.isProcessing = false;
          return;
        }

        const extension = compressedBlob.type === 'image/jpeg' ? 'jpg' : 'png';
        const compressedFile = new File(
          [compressedBlob],
          `compressed-image.${extension}`,
          { type: compressedBlob.type }
        );

        this.$emit('compressed', compressedFile);
        this.closeModal();
      } catch (error) {
        console.error('Compression error:', error);
        this.compressionError = '圧縮処理中にエラーが発生しました。';
      } finally {
        this.isProcessing = false;
      }
    },

    /**
     * Canvas画像を目標サイズ以下に圧縮
     * @param {HTMLCanvasElement} canvas - 圧縮するCanvas
     * @param {number} targetSize - 目標サイズ(バイト)
     * @param {string} preferredFormat - 優先形式('jpeg' or 'png')
     * @returns {Promise<Blob|null>} 圧縮されたBlob
     */
    async compressToTargetSize(canvas, targetSize, preferredFormat = 'jpeg') {
      // 優先形式がPNGの場合、まずPNGで試す
      if (preferredFormat === 'png') {
        const blob = await this.canvasToBlob(canvas, 'image/png');
        if (blob.size <= targetSize) {
          return blob;
        }
      }

      // JPEG品質を細かく調整(より高品質から開始)
      const qualities = [1.0, 0.95, 0.9, 0.85, 0.8, 0.75, 0.7, 0.65, 0.6, 0.55, 0.5, 0.45, 0.4];

      for (const quality of qualities) {
        const blob = await this.canvasToBlob(canvas, 'image/jpeg', quality);
        if (blob.size <= targetSize) {
          return blob;
        }
      }

      // 優先形式がJPEGで、まだPNGを試していない場合は試す
      if (preferredFormat === 'jpeg') {
        const blob = await this.canvasToBlob(canvas, 'image/png');
        if (blob.size <= targetSize) {
          return blob;
        }
      }

      // それでも大きい場合、縦横比を維持したまま緩やかに縮小して再試行
      const scales = [0.95, 0.9, 0.85, 0.8, 0.75, 0.7];
      for (const scale of scales) {
        const scaledWidth = Math.round(this.sourceWidth * scale);
        const scaledHeight = Math.round(this.sourceHeight * scale);
        const scaledCanvas = this.resizeCanvas(canvas, scaledWidth, scaledHeight);

        // 優先形式がPNGの場合、スケール後もまずPNGで試す
        if (preferredFormat === 'png') {
          const blob = await this.canvasToBlob(scaledCanvas, 'image/png');
          if (blob.size <= targetSize) {
            return blob;
          }
        }

        // PNGでダメな場合、またはJPEG優先の場合はJPEG品質調整を試行
        for (const quality of qualities) {
          const blob = await this.canvasToBlob(scaledCanvas, 'image/jpeg', quality);
          if (blob.size <= targetSize) {
            return blob;
          }
        }
      }

      return null;
    },

    /**
     * CanvasをBlobに変換
     */
    canvasToBlob(canvas, mimeType, quality = 1) {
      return new Promise((resolve) => {
        canvas.toBlob(resolve, mimeType, quality);
      });
    },

    /**
     * Canvasを指定サイズにリサイズ(縦横比は呼び出し側で保証)
     */
    resizeCanvas(sourceCanvas, width, height) {
      const resizedCanvas = document.createElement('canvas');
      resizedCanvas.width = width;
      resizedCanvas.height = height;
      const ctx = resizedCanvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(sourceCanvas, 0, 0, width, height);
      return resizedCanvas;
    },
  },
};
</script>

<style scoped>
.c-modal__content--large {
  max-width: 600px;
  width: auto;
  max-height: 90vh !important;
  overflow-y: auto !important;
}

.c-modal__title--bold {
  font-weight: bold;
}

.c-modal__description {
  text-align: center;
  color: #666;
  font-size: 14px;
  margin-top: 10px;
  margin-bottom: 15px;
}

.c-image-compress {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

.c-image-compress__preview {
  max-width: 100%;
  max-height: 400px;
  overflow: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.c-image-compress__preview img {
  display: block;
  max-width: 100%;
  height: auto;
}

.c-image-compress__processing {
  text-align: center;
  color: #007bff;
  font-weight: bold;
  padding: 10px;
}

.c-image-compress__error {
  padding: 10px;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  font-size: 14px;
}
</style>
